<template>
  <BaseModal v-model="model" :width="width" @closed="handleBaseModalClosed">
    <ModalCard :title="title" :data-test-id="dataTestId" :help-link="helpLink">
      <div v-if="icon">
        <div class="confirm-icon mb-4 text-center">
          <v-icon :color="color">{{ icon }}</v-icon>
        </div>
      </div>
      <div
        class="text-sm font-medium text-app-black"
        :class="{ 'text-center': icon }"
      >
        <slot>
          <span class="whitespace-pre-wrap">{{ t(message || '') }}</span>
        </slot>
      </div>

      <template #footer>
        <ModalFormButtons
          :loading="loading"
          :color="color"
          :disabled-submit="disabled"
          :form="formId"
          :cancel-text="cancelLabel ? $t(cancelLabel) : undefined"
          :hide-cancel="hideCancel"
          :submit-text="okLabel ? $t(okLabel || '') : undefined"
          @click:cancel="handleClose(false)"
          @click:submit="handleClose(true)"
        />
      </template>
    </ModalCard>
  </BaseModal>
</template>

<script lang="ts" setup>
import { useTranslate } from '@manager/composables/use-translate';
import BaseModal from '@manager/modules/modals/base/BaseModal.vue';
import ModalCard from '@manager/modules/modals/base/ModalCard.vue';
import ModalFormButtons from '@manager/modules/modals/base/ModalFormButtons.vue';
import type { ITableRowConfirmData } from '@manager/types/action-menu-types';
import type { IHelpLink } from 'shared/ui/registry/kinnovis/ui-help-link/UIHelpLink.vue';
import { computed } from 'vue';

const { t } = useTranslate();

interface Props {
  title?: ITableRowConfirmData['title'];
  icon?: string;
  message?: ITableRowConfirmData['message'];
  color?: ITableRowConfirmData['color'];
  loading?: boolean;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'unset';
  resolve?: (value: boolean) => void;
  okLabel?: ITableRowConfirmData['okLabel'];
  cancelLabel?: ITableRowConfirmData['cancelLabel'];
  dataTestId?: string;
  disabled?: boolean;
  helpLink?: IHelpLink;
  formId?: string;
  hideCancel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  icon: undefined,
  message: undefined,
  color: 'primary',
  loading: false,
  width: 'md',
  resolve: undefined,
  okLabel: 'confirmDialog.ok',
  cancelLabel: 'confirmDialog.cancel',
  dataTestId: 'confirm-dialog',
  disabled: false,
  helpLink: undefined,
  formId: undefined,
  hideCancel: false,
});

const model = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  closed: [boolean];
  'after-close': [];
}>();

const loading = computed(() => props.loading);

function handleClose(value: boolean): void {
  emit('closed', value);
  props.resolve?.(value);
}

function handleBaseModalClosed(): void {
  emit('after-close');
}
</script>

<style scoped lang="scss">
.confirm-body {
  line-height: 1.8;
}

.confirm-icon :deep(.v-icon) {
  font-size: 60px;
}
</style>
